// src/data/coverage/measureDisplayNames.ts
// ============================================================
// MEASURE DISPLAY NAMES — the only sanctioned way to put a measure name on screen
//
// NEVER render a raw catalog name in a user-facing surface. Go through displayName(id).
//
// WHY THE OVERRIDES EXIST — three separate reasons, do not collapse them:
//
//   29, 54 and 55 are overridden for the SAME reason: their catalog names contain terms
//   that are not permitted in external copy. This is an editorial constraint, not a data
//   defect. The catalog string is correct in the warehouse and must stay as it is there;
//   only the rendered string changes.
//     29  catalog "Data Cleansing and Transformation" -> "Data Cleansing and Preparation"
//     54  catalog "Stakeholder Engagement"            -> "Sponsor Coverage"
//     55  catalog "Pilot Implementation Feasibility"  -> "Initial Deployment Feasibility"
//
//   46 is overridden for a spelling and punctuation defect. The raw dva-stage catalog
//   carries the misspelling "Resource Avaliablity & Allocation". The in-repo fixture
//   (prototype/data/fixtures/ccRationaleStage.ts) already corrects the spelling to
//   "Resource Availability & Allocation" but keeps the ampersand, so an override is still
//   required to reach the display form.
//     46  catalog "Resource Availability & Allocation" -> "Resource Availability and Allocation"
//
//   56 is listed for completeness and is currently a no-op. The catalog string already
//   reads "Ease of Regulatory and Compliance Approval", which is the intended display
//   form. The documented stage defect on 56 is a trailing space, which trim() handles.
//     56  catalog "Ease of Regulatory and Compliance Approval" -> unchanged
//
// CATALOG_NAMES below mirrors CC_MEASURE_RATIONALES in
// src/prototype/data/fixtures/ccRationaleStage.ts. It is duplicated rather than imported
// because src/data must not reach into src/prototype. A drift test asserts the two agree
// name for name, so the mirror cannot go stale unnoticed.
// ============================================================

import { COVERAGE_MEASURE_IDS } from './measureGroupMap';

/** Catalog names, verbatim from the in-repo 38-row catalog mirror. Not for display. */
export const CATALOG_NAMES: Readonly<Record<number, string>> = {
  1: 'Revenue Generation',
  2: 'Cost Savings',
  3: 'Long-Term Value Creation',
  4: 'Challenges',
  5: 'Core Mission Support',
  6: 'Competitive Differentiation',
  7: 'Value-Based Care Integration',
  8: 'Strategic Growth and Long-Term Viability',
  21: 'Operational Efficiency',
  22: 'Technology Alignment',
  24: 'Revenue Diversification',
  26: 'Data Quality and Completeness',
  27: 'Data Standardization',
  28: 'Data Accessibility and Integration',
  29: 'Data Cleansing and Transformation',
  30: 'Data Interoperability and Connectivity',
  31: 'Historical Data Availability',
  32: 'Real-Time Data Capabilities',
  34: 'Data Security and Privacy Compliance',
  35: 'Data De-Identification and Anonymization Needs',
  36: 'Regulatory Framework Alignment',
  37: 'Legal and Contractual Clarity',
  38: 'Auditability and Reporting Requirements',
  39: 'Third-Party and Vendor Compliance',
  40: 'Regulatory Complexity Across Jurisdictions',
  41: 'Patient Consent Requirements',
  42: 'Technical Infrastructure',
  43: 'Staff Expertise',
  44: 'Organizational Processes',
  45: 'Change Management Capability',
  46: 'Resource Availability & Allocation',
  47: 'Partnerships & Engagement',
  51: 'Implementation Complexity',
  52: 'Market Readiness',
  53: 'Speed to Generate Returns',
  54: 'Stakeholder Engagement',
  55: 'Pilot Implementation Feasibility',
  56: 'Ease of Regulatory and Compliance Approval',
};

/** Display overrides. Only ids that need one. See the header for why each is here. */
export const MEASURE_DISPLAY_OVERRIDES: Readonly<Record<number, string>> = {
  29: 'Data Cleansing and Preparation',
  46: 'Resource Availability and Allocation',
  54: 'Sponsor Coverage',
  55: 'Initial Deployment Feasibility',
  56: 'Ease of Regulatory and Compliance Approval',
};

/**
 * THE ONLY SANCTIONED ACCESSOR for a measure name on a user-facing surface.
 *
 * Falls back to the catalog name when no override exists. Returns a stable placeholder
 * rather than an empty string for an unknown id, so a bad id shows up on screen as an
 * obvious defect instead of a blank cell.
 */
export function displayName(measureId: number): string {
  const override = MEASURE_DISPLAY_OVERRIDES[measureId];
  if (override) return override.trim();
  const catalog = CATALOG_NAMES[measureId];
  if (catalog) return catalog.trim();
  return `Measure ${measureId}`;
}

/**
 * displayName for a callers that hold the warehouse key rather than the numeric id.
 *
 * Row-level fixtures and adapters carry `measure_id` as `m54`, not `54`. Parsing that at
 * each render site would put the same two lines in every component and give each one its
 * own chance to get it wrong, so the parse lives here with the accessor it feeds.
 *
 * An unparseable key returns the same visible placeholder displayName does, so a bad key
 * shows up on screen as an obvious defect rather than as a blank.
 */
export function displayNameForKey(key: string | number): string {
  if (typeof key === 'number') return displayName(key);
  const digits = /^m?(\d+)$/.exec(String(key).trim());
  return digits ? displayName(Number(digits[1])) : `Measure ${key}`;
}

/** True when the id is one of the 38 active measures. */
export function isKnownMeasure(measureId: number): boolean {
  return COVERAGE_MEASURE_IDS.includes(measureId);
}
